import safeExecute from '@src/util/safeExecute';
import { sendErrorToSentry } from '@src/util/sentry/utils';

const isGeocodingNetworkFailure = error =>
  error instanceof TypeError && /failed to fetch/i.test(String(error.message || ''));

class GeocodingRepository {
  constructor() {
    this.baseUrl = 'https://nominatim.openstreetmap.org';
    this.headers = {
      'User-Agent': 'PWA-App/1.0 (contact@operonda.com)',
      'Accept-Language': 'es',
    };
  }

  /**
   * Nominatim fetch that soft-fails on network/HTTP errors (no Sentry spam).
   * Unexpected failures (e.g. JSON parse) are reported then rethrown.
   * @param {string} url
   * @returns {Promise<any|null>}
   */
  async softFetchNominatim(url) {
    try {
      const response = await fetch(url, { headers: this.headers });
      if (!response.ok) {
        return null;
      }
      return await response.json();
    } catch (error) {
      if (isGeocodingNetworkFailure(error)) {
        return null;
      }
      sendErrorToSentry(error, { functionName: 'softFetchNominatim', url });
      throw error;
    }
  }

  /**
   * Search countries by query
   * @param {string} query - Search query
   * @returns {Promise<Array>} List of countries
   */
  async searchCountries(query) {
    const url = `${this.baseUrl}/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=10&featuretype=country`;
    const result = await this.softFetchNominatim(url);
    return result ?? [];
  }

  /**
   * Search cities by country and city query
   * @param {string} country - Country name
   * @param {string} cityQuery - City search query
   * @returns {Promise<Array>} List of cities
   */
  async searchCities(country, cityQuery) {
    const url = `${this.baseUrl}/search?country=${encodeURIComponent(country)}&city=${encodeURIComponent(cityQuery)}&format=json&addressdetails=1&limit=10`;
    const result = await this.softFetchNominatim(url);
    return result ?? [];
  }

  /**
   * Search states/provinces by country
   * @param {string} country - Country name
   * @param {string} stateQuery - State search query
   * @returns {Promise<Array>} List of states/provinces
   */
  async searchStates(country, stateQuery) {
    const url = `${this.baseUrl}/search?state=${encodeURIComponent(stateQuery)}&country=${encodeURIComponent(country)}&format=json&addressdetails=1&limit=20`;
    const result = await this.softFetchNominatim(url);
    return result ?? [];
  }

  /**
   * Search addresses by country, city and street
   * @param {string} country - Country name
   * @param {string} city - City name
   * @param {string} street - Street name
   * @returns {Promise<Array>} List of addresses
   */
  async searchAddress(country, city, street) {
    const url = `${this.baseUrl}/search?country=${encodeURIComponent(country)}&city=${encodeURIComponent(city)}&street=${encodeURIComponent(street)}&format=json&addressdetails=1&limit=5`;
    const result = await this.softFetchNominatim(url);
    return result ?? [];
  }

  /**
   * Free text search using Google Places API
   * Converts Google Places format to Nominatim format for compatibility
   * @param {string} query - Free text search query
   * @returns {Promise<Array>} List of locations
   */
  async searchFreeTextGoogle(query) {
    return safeExecute(async () => {
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        throw new Error('Google Maps Places API not loaded');
      }

      return new Promise((resolve, reject) => {
        const service = new window.google.maps.places.AutocompleteService();
        
        service.getPlacePredictions(
          {
            input: query,
            types: ['geocode'],
          },
          (predictions, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
              const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
              const results = [];
              let completed = 0;
              
              if (predictions.length === 0) {
                resolve([]);
                return;
              }

              predictions.slice(0, 10).forEach((prediction, index) => {
                placesService.getDetails(
                  {
                    placeId: prediction.place_id,
                    fields: ['geometry', 'address_components', 'formatted_address'],
                  },
                  (place, placeStatus) => {
                    if (placeStatus === window.google.maps.places.PlacesServiceStatus.OK && place) {
                      const addressComponents = place.address_components || [];
                      const address = {};
                      
                      addressComponents.forEach(component => {
                        const types = component.types;
                        if (types.includes('street_number')) {
                          address.house_number = component.long_name;
                        }
                        if (types.includes('route')) {
                          address.road = component.long_name;
                        }
                        if (types.includes('locality')) {
                          address.city = component.long_name;
                        }
                        if (types.includes('administrative_area_level_2')) {
                          address.city = address.city || component.long_name;
                        }
                        if (types.includes('administrative_area_level_1')) {
                          address.state = component.long_name;
                        }
                        if (types.includes('country')) {
                          address.country = component.long_name;
                          address.country_code = component.short_name.toLowerCase();
                        }
                        if (types.includes('postal_code')) {
                          address.postcode = component.long_name;
                        }
                        if (types.includes('sublocality') || types.includes('sublocality_level_1')) {
                          address.suburb = component.long_name;
                        }
                      });

                      results[index] = {
                        display_name: place.formatted_address || prediction.description,
                        address: address,
                        lat: place.geometry?.location?.lat()?.toString() || '',
                        lon: place.geometry?.location?.lng()?.toString() || '',
                      };
                    }
                    
                    completed++;
                    if (completed === Math.min(predictions.length, 10)) {
                      resolve(results.filter(r => r !== undefined));
                    }
                  }
                );
              });
            } else {
              reject(new Error(`Places search failed: ${status}`));
            }
          }
        );
      });
    });
  }

  /**
   * Free text search for any location
   * Uses Google Places API
   * @param {string} query - Free text search query
   * @returns {Promise<Array>} List of locations
   */
  async searchFreeText(query) {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      throw new Error('Google Maps Places API not loaded');
    }

    return await this.searchFreeTextGoogle(query);
  }

  /**
   * Reverse geocoding using Google Places API
   * Uses PlacesService.findPlaceFromQuery with coordinates as query
   * Converts Google Places format to Nominatim format for compatibility
   * @param {number} lat - Latitude
   * @param {number} lon - Longitude
   * @returns {Promise<Object>} Address details in Nominatim format
   */
  async reverseGeocodeGoogle(lat, lon) {
    return safeExecute(async () => {
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        throw new Error('Google Maps Places API not loaded');
      }

      return new Promise((resolve, reject) => {
        const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
        const location = new window.google.maps.LatLng(lat, lon);
        const query = `${lat},${lon}`;

        placesService.findPlaceFromQuery(
          {
            query: query,
            fields: ['formatted_address', 'address_components', 'geometry'],
            locationBias: location,
          },
          (results, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
              const place = results[0];
              const addressComponents = place.address_components || [];
              const address = {};
              
              addressComponents.forEach(component => {
                const types = component.types;
                if (types.includes('street_number')) {
                  address.house_number = component.long_name;
                }
                if (types.includes('route')) {
                  address.road = component.long_name;
                }
                if (types.includes('locality')) {
                  address.city = component.long_name;
                }
                if (types.includes('administrative_area_level_2')) {
                  address.city = address.city || component.long_name;
                }
                if (types.includes('administrative_area_level_1')) {
                  address.state = component.long_name;
                }
                if (types.includes('country')) {
                  address.country = component.long_name;
                  address.country_code = component.short_name.toLowerCase();
                }
                if (types.includes('postal_code')) {
                  address.postcode = component.long_name;
                }
                if (types.includes('sublocality') || types.includes('sublocality_level_1')) {
                  address.suburb = component.long_name;
                }
              });

              resolve({
                display_name: place.formatted_address,
                address: address,
                lat: lat.toString(),
                lon: lon.toString(),
              });
            } else {
              reject(new Error(`Places findPlaceFromQuery failed: ${status}`));
            }
          }
        );
      });
    });
  }

  /**
   * Reverse geocoding - get address from coordinates
   * Uses Nominatim (OpenStreetMap). Network/HTTP failures return null (no Sentry).
   * @param {number} lat - Latitude
   * @param {number} lon - Longitude
   * @returns {Promise<Object|null>} Address details
   */
  async reverseGeocode(lat, lon) {
    const url = `${this.baseUrl}/reverse?lat=${lat}&lon=${lon}&format=json&addressdetails=1`;
    return this.softFetchNominatim(url);
  }

  /**
   * Search structured address
   * @param {Object} addressParts - Address components
   * @param {string} addressParts.street - Street name
   * @param {string} addressParts.city - City name
   * @param {string} addressParts.state - State/region name
   * @param {string} addressParts.country - Country name
   * @param {string} addressParts.postalcode - Postal code
   * @returns {Promise<Array>} List of matching addresses
   */
  async searchStructured(addressParts) {
    const params = new URLSearchParams({
      format: 'json',
      addressdetails: '1',
      limit: '5',
    });

    if (addressParts.street) params.append('street', addressParts.street);
    if (addressParts.city) params.append('city', addressParts.city);
    if (addressParts.state) params.append('state', addressParts.state);
    if (addressParts.country) params.append('country', addressParts.country);
    if (addressParts.postalcode) params.append('postalcode', addressParts.postalcode);

    const url = `${this.baseUrl}/search?${params.toString()}`;
    const result = await this.softFetchNominatim(url);
    return result ?? [];
  }
}

export default GeocodingRepository;

